import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BiTrash, BiCommentEdit, BiExit, BiSave } from 'react-icons/bi';
import { useQuery, useMutation } from '@apollo/client';
import { useAuthContext } from '../hooks/useAuthContext';
import { GET_POST } from '../queries/postQueries';
import { GET_USER_PROFILE } from '../queries/userQueries';
import { UPDATE_COMMENT, DELETE_COMMENT } from '../mutations/commentMutations';

import Reply from './Reply';
import AddReplyButton from './AddReplyButton';
import EmojiPicker from './EmojiPicker';
import { useOnClickOutside } from 'usehooks-ts';
import Spinner from './Spinner';

export default function Comment({ comment, refetchPost }) {
  const [expandedComments, setExpandedComments] = useState([])
  const [content, setContent] = useState('')
  const [showAllReplies, setShowAllReplies] = useState(false)
  const [editMode, setEditMode] = useState(false)

  const { authUser } = useAuthContext()
  const navigate = useNavigate()
  const commentRef = useRef(null)
  const date = new Date(comment.createdAt)

  const { loading: postLoading, error: postError, data: postData } = useQuery(GET_POST, {
    variables: { id: comment.postId }
  })

  const { loading: userLoading, error: userError, data: userData } = useQuery(GET_USER_PROFILE, {
    variables: { id: comment.userId }
  })

  const [updateComment] = useMutation(UPDATE_COMMENT, {
    variables: { id: comment.id, content },
    onCompleted: () => {
      refetchPost()
    },
    onError: (error) => {
      throw new Error(error.message)
    }
  })

  const [deleteComment] = useMutation(DELETE_COMMENT, {
    variables: { id: comment.id },
    onCompleted: () => {
      refetchPost()
    },
    onError: (error) => {
      throw new Error(error.message)
    }
  })

  const showComment = (commentId) => {
    if (expandedComments.includes(commentId)) {
      setExpandedComments(expandedComments.filter((id) => id !== commentId))
    } else {
      setExpandedComments([...expandedComments, commentId])
    }
  }

  const writeComment = () => {
    if (content === '') {
      return alert('Please fill up the content')
    }

    updateComment({ variables: { id: comment.id, content } })

    setEditMode(false)
    setContent('')
  }

  const trashComment = () => {
    deleteComment({ variables: { id: comment.id } })
  }

  const handleClickOutside = (e) => {
    if (commentRef.current && !commentRef.current.contains(e.target)) {
      e.stopPropagation()
      setEditMode(false)
    }
  }

  useOnClickOutside(commentRef, handleClickOutside)

  if (postError) return <p>Error loading post data: {postError.message}</p>

  const isPostOwner = () => {
    if (!postLoading) {
      if (authUser.id === postData.post.userId) {
        return true
      }
      return false
    }

    return false
  }

  if (userError) return <p>Error loading user data: {userError.message}</p>

  const isCommentOwner = () => {
    if (authUser.id === comment.userId) {
      return true
    }
    return false
  }

  const repliesToShow = showAllReplies ? comment.replies : comment.replies.slice(0, 0)

  const handleShowMoreReplies = () => {
    setShowAllReplies(!showAllReplies)
  }

  return (
    <div ref={commentRef} className='comment-section bg-secondary d-flex row my-1 p-1 border border-secondary rounded'>
      {userLoading ? (
        <div>
          <Spinner marginSize='10px' />
        </div>
      ) : (<>
        <div>
          <div className="d-flex flex-column justify-content-between mt-1">
            {/*<p className="text-end rfs-5">{date.toLocaleDateString()} - {date.toLocaleTimeString()}</p>*/}
            <p className='rfs-4'>
              <span className="cursor-pointer" onClick={() => navigate(`/users/${userData.userProfile.id}`)}>@{userData.userProfile.username}</span>
            </p>
          </div>

          <div className='mb-3'>
            {editMode ? (
              <div className='d-flex container'>
                <EmojiPicker content={content} setContent={setContent} />

                <input
                  className='form-control rfs-5'
                  style={{ borderStyle: 'hidden' }}
                  id='content'
                  value={content}
                  autoFocus
                  onChange={(e) => setContent(e.target.value)}
                ></input>
              </div>
            ) : (
              <p className='form-control text-white rfs-5' style={{ backgroundColor: 'inherit', borderStyle: 'hidden' }}>
                {
                  comment.content.length < 50 ? comment.content : (
                    expandedComments.includes(comment.id) ? comment.content : `${comment.content.slice(0, 50)}...`
                  )
                }
              </p>
            )}
          </div>

          <div className="comment-wrp">
            <div className="comment container">
              {editMode ? (
                <div className="c-controls">
                  <BiSave className="icon rfs-3 cursor-pointer text-white me-2" onClick={() => writeComment()} />
                  <BiExit className="icon rfs-3 cursor-pointer text-white" onClick={() => setEditMode(false)} />
                </div>
              ) : (
                <div className="c-controls">
                  {(isPostOwner() || isCommentOwner()) && (
                    <BiTrash className="icon rfs-3 cursor-pointer text-white me-2" onClick={trashComment} />
                  )}
                  {isCommentOwner() && (
                    <BiCommentEdit className="icon rfs-3 cursor-pointer text-white me-2" onClick={() => setEditMode(true)} />
                  )}

                  <AddReplyButton commentId={comment.id} postId={comment.postId} refetchPost={refetchPost} />
                </div>
              )}
            </div>

            <div className="replies comments-wrp">
              {comment.replies.length > 0 && (
                <button className='btn btn-link rfs-6 text-rose' onClick={handleShowMoreReplies}>
                  {showAllReplies ? 'Hide Replies' : 'Show Replies'}
                </button>
              )}

              {repliesToShow.map((reply, index) => (
                <Reply key={index} reply={reply} refetchPost={refetchPost} />
              ))}
            </div>
          </div>
        </div>

        {comment.content.length > 50 && !editMode &&
          <button className='btn btn-link rfs-5 text-rose' onClick={() => showComment(comment.id)}>
            {expandedComments.includes(comment.id) ? "Hide Comment" : "Show Comment"}
          </button>
        }
      </>)}
    </div>
  )
}
